import type {CalypsoCatalog} from "#pricing/catalogs/calypso/catalog"
import type {KeyedVariants} from "#pricing/catalogs/graph-helpers"
import {
    createAmountTriggeredPriceNode,
    createConditionedPriceNodes,
    createKeyedVariantPriceNodes,
    createPriceContainer,
    createVariantConditionedPriceNodes,
    not,
    variantSelected,
} from "#pricing/catalogs/graph-helpers"
import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import {ConfigurationList} from "#pricing/nodes/configuration-list"
import {MeshAmountNode} from "#pricing/nodes/mesh-amount-node"
import type {MeshesNode} from "#pricing/nodes/meshes-node"
import type {PriceContainer} from "#pricing/nodes/price-container"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"

export function createMirrorContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const altino400Amount = new MeshAmountNode({parameters: {name: "3373 - Altino 400", meshes: Meshes}})
    const altino400 = createAmountTriggeredPriceNode(catalog, "altino led mirror, 4", altino400Amount)

    const altino500Amount = new MeshAmountNode({parameters: {name: "3374 - Altino 500", meshes: Meshes}})
    const altino500 = createAmountTriggeredPriceNode(catalog, "altino led mirror, 5", altino500Amount)

    const altino600Amount = new MeshAmountNode({parameters: {name: "3375 - Altino 600", meshes: Meshes}})
    const altino600 = createAmountTriggeredPriceNode(catalog, "altino led mirror, 6", altino600Amount)

    const farnese500Amount = new MeshAmountNode({parameters: {name: "5709 - Farnese - 500", meshes: Meshes}})
    const farnese500 = createAmountTriggeredPriceNode(catalog, "farnese mirrored cabinet, 5", farnese500Amount)

    const farnese600Amount = new MeshAmountNode({parameters: {name: "5589 - Farnese - 600", meshes: Meshes}})
    const farnese600 = createAmountTriggeredPriceNode(catalog, "farnese mirrored cabinet, 6", farnese600Amount)

    const farnese750Amount = new MeshAmountNode({parameters: {name: "5710 - Farnese Mirrored Cabinet 750 x 195 x 700mm", meshes: Meshes}})
    const farnese750 = createAmountTriggeredPriceNode(catalog, "farnese mirrored cabinet, 7", farnese750Amount)

    const farnese900Amount = new MeshAmountNode({parameters: {name: "5711 - Farnese", meshes: Meshes}})
    const farnese900 = createAmountTriggeredPriceNode(catalog, "farnese mirrored cabinet, 9", farnese900Amount)

    const farnese1200Amount = new MeshAmountNode({parameters: {name: "5712 - Farnese Mirrored Cabinet 1200 x 195 x 700mm", meshes: Meshes}})
    const farnese1200 = createAmountTriggeredPriceNode(catalog, "farnese mirrored cabinet, 1", farnese1200Amount)

    const portel700Amount = new MeshAmountNode({parameters: {name: "3839 - Portel 700", meshes: Meshes}})
    const portel700 = createAmountTriggeredPriceNode(catalog, "portel led round mirror, 7", portel700Amount)

    const portel900Amount = new MeshAmountNode({parameters: {name: "3359 - Portel 900", meshes: Meshes}})
    const portel900 = createAmountTriggeredPriceNode(catalog, "portel led round mirror, 9", portel900Amount)

    const lugo800Amount = new MeshAmountNode({parameters: {name: "3674 - Lugo 800", meshes: Meshes}})
    const lugo800 = createAmountTriggeredPriceNode(catalog, "lugo led mirror, 600 x 27 x 8", lugo800Amount)

    const lugo1000Amount = new MeshAmountNode({parameters: {name: "3677 - Lugo 1000", meshes: Meshes}})
    const lugo1000 = createAmountTriggeredPriceNode(catalog, "lugo led mirror, 600 x 27 x 10", lugo1000Amount)

    const ostra600Amount = new MeshAmountNode({parameters: {name: "3297 - Ostra 600", meshes: Meshes}})
    const ostra600 = createAmountTriggeredPriceNode(catalog, "ostra led mirror, 6", ostra600Amount)

    const ostra800Amount = new MeshAmountNode({parameters: {name: "3298 - Ostra 800", meshes: Meshes}})
    const ostra800 = createAmountTriggeredPriceNode(catalog, "ostra led mirror, 8", ostra800Amount)

    const ostra1000Amount = new MeshAmountNode({parameters: {name: "3299 - Ostra 1000", meshes: Meshes}})
    const ostra1000 = createAmountTriggeredPriceNode(catalog, "ostra led mirror, 10", ostra1000Amount)

    const ostra1200Amount = new MeshAmountNode({parameters: {name: "3300 - Ostra 1200", meshes: Meshes}})
    const ostra1200 = createAmountTriggeredPriceNode(catalog, "ostra led mirror, 12", ostra1200Amount)

    const caminoAmount = new MeshAmountNode({parameters: {name: "3629 - Camino LED Mirror Dimmable", meshes: Meshes}})
    const camino = createAmountTriggeredPriceNode(catalog, "camino led mirror", caminoAmount)

    const ferno600Amount = new MeshAmountNode({parameters: {name: "3630 - Ferno 600", meshes: Meshes}})
    const ferno600 = createAmountTriggeredPriceNode(catalog, "ferno led mirror dimmable/temp change, 6", ferno600Amount)

    const ferno800Amount = new MeshAmountNode({parameters: {name: "3631 - Ferno 800", meshes: Meshes}})
    const ferno800 = createAmountTriggeredPriceNode(catalog, "ferno led mirror dimmable/temp change, 8", ferno800Amount)

    const ferno1000Amount = new MeshAmountNode({parameters: {name: "3632 - Ferno 1000", meshes: Meshes}})
    const ferno1000 = createAmountTriggeredPriceNode(catalog, "ferno led mirror dimmable/temp change, 10", ferno1000Amount)

    const ferno1200Amount = new MeshAmountNode({parameters: {name: "3633 - Ferno 1200", meshes: Meshes}})
    const ferno1200 = createAmountTriggeredPriceNode(catalog, "ferno led mirror dimmable/temp change, 12", ferno1200Amount)

    const ostuni600Amount = new MeshAmountNode({parameters: {name: "3830 - Ostuni 600", meshes: Meshes}})
    const ostuni600 = createAmountTriggeredPriceNode(catalog, "ostuni led mirror dimmable/temp change, 6", ostuni600Amount)

    const ostuni800Amount = new MeshAmountNode({parameters: {name: "3831 - Ostuni 800", meshes: Meshes}})
    const ostuni800 = createAmountTriggeredPriceNode(catalog, "ostuni led mirror dimmable/temp change, 8", ostuni800Amount)

    const ostuni1000Amount = new MeshAmountNode({parameters: {name: "3832 - Ostuni 1000", meshes: Meshes}})
    const ostuni1000 = createAmountTriggeredPriceNode(catalog, "ostuni led mirror dimmable/temp change, 10", ostuni1000Amount)

    const ostuni1200Amount = new MeshAmountNode({parameters: {name: "3833 - Ostuni 1200", meshes: Meshes}})
    const ostuni1200 = createAmountTriggeredPriceNode(catalog, "ostuni led mirror dimmable/temp change, 12", ostuni1200Amount)

    const lixa600Amount = new MeshAmountNode({parameters: {name: "3801 - Lixa LED MIrror 600 x 900mm", meshes: Meshes}})
    const lixa600 = createAmountTriggeredPriceNode(catalog, "lixa led mirror, 6", lixa600Amount)

    const lixa740Amount = new MeshAmountNode({parameters: {name: "3370 - Lixa LED MIrror 740 x 900mm_main", meshes: Meshes}})
    const lixa740 = createAmountTriggeredPriceNode(catalog, "lixa led mirror, 7", lixa740Amount)

    const lixa1000Amount = new MeshAmountNode({parameters: {name: "3371 - Lixa LED MIrror 1000 x 900mm_main", meshes: Meshes}})
    const lixa1000 = createAmountTriggeredPriceNode(catalog, "lixa led mirror, 10", lixa1000Amount)

    const lixa1200Amount = new MeshAmountNode({parameters: {name: "3372 - Lixa LED Mirror 1200 x 900mm_main", meshes: Meshes}})
    const lixa1200 = createAmountTriggeredPriceNode(catalog, "lixa led mirror, 12", lixa1200Amount)

    const taro900Amount = new MeshAmountNode({parameters: {name: "3349 - Taro LED Mirror 900 x 900mm cw Demister", meshes: Meshes}})
    const taro900 = createAmountTriggeredPriceNode(catalog, "taro led mirror", taro900Amount)

    const torla550Amount = new MeshAmountNode({
        parameters: {name: "3680 - Torla LED Mirror 550 x 130 x 1000 Touch Control & Demister Gold (550 x 1000)", meshes: Meshes},
    })
    const torla550 = createAmountTriggeredPriceNode(catalog, "torla led mirror", torla550Amount)

    return createPriceContainer(
        "Mirrors",
        "Mirrors",
        [
            altino400,
            altino500,
            altino600,
            farnese500,
            farnese600,
            farnese750,
            farnese900,
            farnese1200,
            portel700,
            portel900,
            lugo800,
            lugo1000,
            ostra600,
            ostra800,
            ostra1000,
            ostra1200,
            camino,
            ferno600,
            ferno800,
            ferno1000,
            ferno1200,
            ostuni600,
            ostuni800,
            ostuni1000,
            ostuni1200,
            lixa600,
            lixa740,
            lixa1000,
            lixa1200,
            taro900,
            torla550,
        ],
        true,
    )
}

/** The Velas round mirrors only come in glass, so mesh presence alone prices them. */
export function createVelasMirrorContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const velas600Amount = new MeshAmountNode({parameters: {name: "3397 - Velas LED Round Mirror 600mm c-w Demister & Switch", meshes: Meshes}})
    const velas600 = createAmountTriggeredPriceNode(catalog, "3397", velas600Amount)

    const velas700Amount = new MeshAmountNode({parameters: {name: "3398 - Velas LED Round Mirror 700mm c-w Demister & Switch", meshes: Meshes}})
    const velas700 = createAmountTriggeredPriceNode(catalog, "3398", velas700Amount)

    return createPriceContainer("Velas Mirrors", "Velas Mirrors", [velas600, velas700], true)
}

/** The Orba mirror only comes in glass, so mesh presence alone prices it. */
export function createOrbaMirrorContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const orbaAmount = new MeshAmountNode({parameters: {name: "3348 - Orba LED Mirror 1000 x 1000mm c-w Demister", meshes: Meshes}})
    const orba = createAmountTriggeredPriceNode(catalog, "3348", orbaAmount)

    return createPriceContainer("Orba Mirror", "Orba Mirror", [orba], true)
}

/**
 * The Murello mirror has one mesh for both frames: the brass variant prices the brushed brass frame (3679), every other finish the black
 * frame (3678). The black node uses a negated condition on the brass variant, so it also prices while a different finish group (e.g. the
 * accessory finish of another tap family) is active. Templates whose Murello always has its own brass/black finish group pass the frame
 * finish as keyed variants instead (description keys "Brushed Brass" / "Black").
 */
export function createMurelloMirrorContainer(catalog: CalypsoCatalog, Meshes: MeshesNode, brass: MirrorFrameFinishVariant | KeyedVariants): PriceContainer {
    const murelloAmount = new MeshAmountNode({parameters: {name: "3678 - Murello LED Mirror 500 x 27 x 800 Touch Control & Demister", meshes: Meshes}})
    if (!Array.isArray(brass)) {
        const murelloUnits = createKeyedVariantPriceNodes(catalog, "Murello", 2, brass, {amount: murelloAmount})
        return createPriceContainer("Murello Mirror", "Murello Mirror", murelloUnits, true)
    }

    const [brassGroup, brassVariantId] = brass

    const [murelloBlack] = createVariantConditionedPriceNodes(catalog, "3678", 1, undefined, murelloAmount)
    const blackCondition = murelloBlack.parameters.condition
    if (!(blackCondition instanceof VariantConditionNode)) throw new Error("Murello node without variant condition")
    blackCondition.parameters.condition.negated = true
    murelloBlack.addDependency(brassGroup, brassVariantId, "")

    const [murelloBrass] = createVariantConditionedPriceNodes(catalog, "3679", 1, undefined, murelloAmount)
    murelloBrass.addDependency(brassGroup, brassVariantId, "")

    return createPriceContainer("Murello Mirror", "Murello Mirror", [murelloBlack, murelloBrass], true)
}

/** A config variant that selects a framed mirror finish. */
export type MirrorFrameFinishVariant = [ConfigurationGroupNode, string]

/**
 * The plain Vittoria is priced by mesh presence. The framed Vittoria shares one mesh for the gold (3835) and black (3836) frame, so the
 * frame finish comes from config variants. Each variant gets its own priced item node: a template may take the frame finish from
 * different groups (e.g. the accessory finish group of the selected tap), only one of which is active at a time.
 */
export function createVittoriaMirrorContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    frameFinishVariants: {gold: MirrorFrameFinishVariant[]; black: MirrorFrameFinishVariant[]},
): PriceContainer {
    const plainAmount = new MeshAmountNode({parameters: {name: "3834 - Vittoria LED Mirror Plain", meshes: Meshes}})
    const plain = createAmountTriggeredPriceNode(catalog, "3834", plainAmount)

    const framedAmount = new MeshAmountNode({parameters: {name: "3835 - Vittoria Framed", meshes: Meshes}})
    const createFramed = (sku: string, variants: MirrorFrameFinishVariant[]) =>
        variants.map(([group, variantId]) => {
            const [node] = createVariantConditionedPriceNodes(catalog, sku, 1, undefined, framedAmount)
            node.addDependency(group, variantId, "")
            return node
        })

    return createPriceContainer(
        "Vittoria Mirrors",
        "Vittoria Mirrors",
        [plain, ...createFramed("3835", frameFinishVariants.gold), ...createFramed("3836", frameFinishVariants.black)],
        true,
    )
}

/** The config group that decides whether a Marbella mirror is framed, with the id of its "With Frame" variant. */
export type MirrorFrameGroup = {group: ConfigurationGroupNode; withFrameVariantId: string}

//One mesh per size carries all three SKUs of that size; the frame is a material on it, not a mesh of its own.
const marbellaSizes = [
    {mesh: "3376 - Marbella - 600, plain", plain: "3376", gold: "3377", black: "3378"},
    {mesh: "3806 - Marbella - 800, plain", plain: "3806", gold: "3807", black: "3808"},
    {mesh: "3809 - Marbella - 1000, plain", plain: "3809", gold: "3810", black: "3811"},
]

/**
 * The Marbella mirrors exist plain, gold framed and black framed, but the template places one mesh per size for all three. The frame
 * comes from the accessory finish of the selected tap: chrome shows no frame at all, any other finish adds the "Mirror Frame" group,
 * which can still be switched back to plain. So the framed SKUs need both the "With Frame" variant and the finish variant, while the
 * plain SKU is priced whenever the frame is *not* on — a negated condition, which also covers the finishes that have no framed SKU
 * (gunmetal, brushed bronze: the template shows them without a frame).
 *
 * Templates with an accessory finish funnel pass the frame finish as keyed variants (description keys "Gold" / "Black"); the conditions
 * are then built with variantSelected instead of VariantConditionNodes.
 */
export function createMarbellaMirrorContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    frame: MirrorFrameGroup,
    frameFinishVariants: {gold: MirrorFrameFinishVariant[]; black: MirrorFrameFinishVariant[]} | KeyedVariants,
): PriceContainer {
    if ("variantIdByKey" in frameFinishVariants) {
        const withFrame = variantSelected(frame.group, frame.withFrameVariantId)
        const subPrices = marbellaSizes.flatMap((size) => {
            const amount = new MeshAmountNode({parameters: {name: size.mesh, meshes: Meshes}})
            const plain = createConditionedPriceNodes(catalog, size.plain, 1, not(withFrame), amount)
            const framed = [size.gold, size.black].flatMap((sku) =>
                createKeyedVariantPriceNodes(catalog, sku, 1, frameFinishVariants, {amount, condition: withFrame}),
            )
            return [...plain, ...framed]
        })
        return createPriceContainer("Marbella Mirrors", "Marbella Mirrors", subPrices, true)
    }

    const subPrices = marbellaSizes.flatMap((size) => {
        const amount = new MeshAmountNode({parameters: {name: size.mesh, meshes: Meshes}})

        const [plain] = createVariantConditionedPriceNodes(catalog, size.plain, 1, undefined, amount)
        plain.updateParameters({
            condition: new VariantConditionNode({
                parameters: {
                    condition: {variantIds: [frame.withFrameVariantId], variantOperator: "AND", negated: true},
                    currentConfigurations: new ConfigurationList({parameters: {list: [frame.group]}}),
                },
            }),
        })

        //A template may take the frame finish from different groups (one accessory finish group per tap family), only one of which is active.
        const createFramed = (sku: string, variants: MirrorFrameFinishVariant[]) =>
            variants.map(([group, variantId]) => {
                const [node] = createVariantConditionedPriceNodes(catalog, sku, 1, undefined, amount)
                node.addDependency(frame.group, frame.withFrameVariantId, "")
                node.addDependency(group, variantId, "")
                return node
            })

        return [plain, ...createFramed(size.gold, frameFinishVariants.gold), ...createFramed(size.black, frameFinishVariants.black)]
    })

    return createPriceContainer("Marbella Mirrors", "Marbella Mirrors", subPrices, true)
}

/** The Andrina mirror comes in one finish only, so mesh presence alone prices it. */
export function createAndrinaMirrorContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const andrinaAmount = new MeshAmountNode({parameters: {name: "3672 - Andrina Octagonal LED Mirror 600 x 30 x 600mm Touch Control", meshes: Meshes}})
    const andrina = createAmountTriggeredPriceNode(catalog, "3672", andrinaAmount)

    return createPriceContainer("Andrina Mirror", "Andrina Mirror", [andrina], true)
}

/**
 * Unlike the shared Marbella sub-template (one mesh per size, see createMarbellaMirrorContainer), the Petra template places separate plain
 * and framed meshes per size, so the mesh decides plain vs. framed and no "Mirror Frame" group is needed. The framed finish comes from the
 * accessory finish funnel (keys "Gold" / "Black" on the SKU descriptions); chrome shows the plain mesh only.
 */
export function createPetraMarbellaMirrorContainer(catalog: CalypsoCatalog, Meshes: MeshesNode, frameFinish: KeyedVariants): PriceContainer {
    const petraMarbellaSizes = [
        {
            plainMesh: "3376 - Marbella LED Mirror Dimmable-Temp Change 600 x 700m c-w Demister",
            framedMesh: "3377 - Marbella LED Mirror Dimmable-Temp Change 600 x 700m c-w Demister",
            plain: "3376",
            gold: "3377",
            black: "3378",
        },
        {
            plainMesh: "3806 - Marbella LED Mirror Dimmable-Temp Change 800 x 700mm c-w Demister",
            framedMesh: "3807 - Marbella LED Mirror Dimmable-Temp Change 800 x 700mm c-w Demister",
            plain: "3806",
            gold: "3807",
            black: "3808",
        },
        {
            plainMesh: "3809 - Marbella LED Mirror Dimmable-Temp Change 1000 x 700mm c-w Demister",
            framedMesh: "3810 - Marbella LED Mirror Dimmable-Temp Change 1000 x 700mm",
            plain: "3809",
            gold: "3810",
            black: "3811",
        },
    ]

    const subPrices = petraMarbellaSizes.flatMap((size) => {
        const plainAmount = new MeshAmountNode({parameters: {name: size.plainMesh, meshes: Meshes}})
        const plain = createAmountTriggeredPriceNode(catalog, size.plain, plainAmount)

        const framedAmount = new MeshAmountNode({parameters: {name: size.framedMesh, meshes: Meshes}})
        const framed = [size.gold, size.black].flatMap((sku) => createKeyedVariantPriceNodes(catalog, sku, 1, frameFinish, {amount: framedAmount}))

        return [plain, ...framed]
    })

    return createPriceContainer("Marbella Mirrors", "Marbella Mirrors", subPrices, true)
}
